import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Store } from '@ngxs/store';
import { cloneDeep, get } from 'lodash';
import {
  AmbassadorState,
  ConfigurationState,
  InspectionDamageDto,
  InspectionImageDto,
  LocationOption,
  PostingDataResponseModel,
  EditPostingState,
  SetImageLocationOptions,
  UpdatePosting,
  UpdateValidationStatusOnPostingFormTracking,
  PostingTypes
} from 'remarketing-angular-library';
import { allLocationCodes, defaultLocationCodes, locationList, tireLocationCodes } from '../data/photos-location-list';
import { TireFormData } from './tires-form.service';

@Injectable({ providedIn: 'root' })
export class EditPostingUtilsService {
  postingData: PostingDataResponseModel;
  inspectionTiresList: TireFormData[];
  inspectionDamageList: InspectionDamageDto[];
  locationOptList: LocationOption[];
  validationFailedForms = new BehaviorSubject<string[]>([]);
  validationFailedFormsArray: string[] = [];

  private readonly AMBASSADOR = 'Ambassador';
  private readonly ADMIN = 'PR_EO_Admin';
  private readonly LEAD_SELLER = 'Lead_Seller';
  private readonly ALLY_PRICE_ADMIN='Ally_Price_Admin';
  private readonly CREDIT_ADMIN = 'Credit_Admin';
  private readonly VEHICLE_ADMIN = 'Vehicle_Admin';
  private readonly EO_ADMIN = 'EO_Admin';


  constructor(
    private readonly store: Store) {
  }

  /* NOTE: This method is not only used for red error icons after clicking post,
           it is also used for New Posting red error icon functionality in navigation.*/
  addToValidationForms(validationStatus: boolean, formName: string) {

    this.updateFormValidationStatusInFormTracking(validationStatus, formName);

    if (!validationStatus) {
      if ((this.validationFailedFormsArray?.filter(p => p === formName).length <= 0)) {
        this.validationFailedFormsArray.push(formName);
        if (this.store.selectSnapshot(EditPostingState.postingType) === PostingTypes.NEW) {
          this.validationFailedForms.next(Object.assign([], this.validationFailedFormsArray));
        }
      }
    } else {
      this.validationFailedFormsArray = this.validationFailedFormsArray.filter(p => p !== formName);
      this.validationFailedForms.next(Object.assign([], this.validationFailedFormsArray));
    }
  }

  updateFormValidationStatusInFormTracking(isValid: boolean, formName: string) {
    this.store.dispatch(new UpdateValidationStatusOnPostingFormTracking(isValid, formName));
  }

  clearFailedValidationFormsArray() {
    this.validationFailedFormsArray = [];
    this.validationFailedForms.next(Object.assign([], this.validationFailedFormsArray));
  }

  displayFailedValidationForms() {
    this.validationFailedForms.next(Object.assign([], this.validationFailedFormsArray));
  }

  /**
   *
   * @param postingData - posting data from API response
   * @returns - default fallback object with inspectionReport/inspectionData is not there
   */
  validateInspectionReport(postingData: PostingDataResponseModel) {
    if (!postingData.inventoryItem.inspectionReport
      || !postingData.inventoryItem.inspectionReport.inspectionData) {
      postingData.inventoryItem.inspectionReport = (postingData.inventoryItem.inspectionReport?.inspectionData) ?
        postingData.inventoryItem.inspectionReport : {
          inspectionData: {
            inspectionCompany: 'DirectInspect',
            vehicle: {
              smokerFlag: null,
              vin: postingData.inventoryItem.vehicleInformation.vin
            }
          },
          itemId: postingData.inventoryItem.itemId
        };

    }
    return cloneDeep(postingData);
  }

  //construct Inspection common, tires and damage photos list
  constructExistingPhotosList(): InspectionImageDto[] {
    const postingData = this.store.selectSnapshot(EditPostingState.updatedPosting);
    const existingImg: InspectionImageDto[] = get(postingData, 'inventoryItem.inspectionReport.inspectionData.images', []);
    const inspectionTiresList = get(postingData, 'inventoryItem.inspectionReport.inspectionData.vehicle.tires', []);
    this.inspectionDamageList = get(postingData, 'inventoryItem.inspectionReport.inspectionData.exceptions', []);
    this.locationOptList = cloneDeep(locationList);
    //constructing tire image list
    const tireImgList = this.constructOtherImageList(inspectionTiresList, 'tireImages');
    //constructing damage image list
    const damageImgList = this.constructOtherImageList(this.inspectionDamageList, 'exceptionImages');
    //saves if dynamic location are added or resets with pre-defined locations
    this.store.dispatch(new SetImageLocationOptions(this.locationOptList));
    return [...existingImg, ...tireImgList, ...damageImgList];
  }

  /**
   *
   * @param inspectionList - list of inspections like tires/damages
   * @param imgProp - property name of image section in inspectionList
   * @returns - image list of incoming inspection with its location and url
   */
  constructOtherImageList(inspectionList, imgProp) {
    const photoList = [];
    //constructing inspection image list
    if (inspectionList) {
      inspectionList.forEach((item, inspectionIndex) => {
        const currLocation = item.location;
        //will add dynamic options only for existing damage form
        if (imgProp === 'exceptionImages') {
          this.addDynamicLocationOptions(currLocation, 'Damage: ');
        }
        if (item[imgProp] && item[imgProp].length) {
          item[imgProp].forEach((imgList) => {
            const imageObj = {
              url: imgList.url || imgList.sourceUrl,
              location: currLocation
            };
            //will add damage item index
            if (imgProp === 'exceptionImages') {
              imageObj['damageIndex'] = inspectionIndex;
            }
            photoList.push(imageObj);
          });
        }
      });
    }
    return photoList;
  }

  /**
   *
   * @param location - location of the inspection
   * @param prefixText - needs to be added in description property
   * Adds the dynamic location option for form like damage and updates the store
   */
  addDynamicLocationOptions(location, prefixText) {
    const isLocationAvailable = this.locationOptList.some((locItem) => {
      return locItem.code === location;
    });
    if (!isLocationAvailable) {
      this.locationOptList.push({
        description: `${prefixText}${location}`,
        code: location
      });
    }
  }

  /**
   *
   * @param modifiedImgList - all inspection images
   * updates the latest inspection image details to the EditPosting state
   */
  updateInspectionImageData(modifiedImgList, saveTires?) {
    this.postingData = this.store.selectSnapshot(EditPostingState.updatedPosting);
    this.inspectionTiresList = get(this.postingData, 'inventoryItem.inspectionReport.inspectionData.vehicle.tires', []);
    this.inspectionDamageList = get(this.postingData, 'inventoryItem.inspectionReport.inspectionData.exceptions', []);

    this.postingData.inventoryItem.inspectionReport.inspectionData.images = modifiedImgList.filter((item) => {
      return defaultLocationCodes.includes(item.location);
    });

    if (this.inspectionTiresList && this.inspectionTiresList.length) {
      this.updateTiresImageData(modifiedImgList);
    }
    if (this.inspectionDamageList && this.inspectionDamageList.length) {
      this.updateDamageImageData(modifiedImgList);
    }
    this.store.dispatch(new UpdatePosting(this.postingData));
  }

  /**
   *
   * @param modifiedImgList - list of all inspection images
   * Saves the latest tires images to updated posting
   */
  updateTiresImageData(modifiedImgList) {
    const tireImages = modifiedImgList.filter((item) => {
      return tireLocationCodes.includes(item.location);
    });
    this.saveFilteredInspectionImage(this.inspectionTiresList, 'tireImages', tireImages);
  }

  /**
   *
   * @param modifiedImgList - contains all the images
   * @param inspetionPropertyName - property name of the image obj
   * selects the respective damage image property and update its latest images
   */
  updateDamageImageData(modifiedImgList) {
    const imageList = modifiedImgList.filter((item) => {
      return !allLocationCodes.includes(item.location);
    });
    this.saveFilteredInspectionImage(this.inspectionDamageList, 'exceptionImages', imageList);
  }

  /**
   *
   * @param inspectionList - inspection list like tires, exceptions etc...
   * @param inspetionPropertyName - inspection image property name
   * @param filteredImages - filtered images like images with tire location etc...
   * selects the newly selected image and save it in posting data
   */
  saveFilteredInspectionImage(inspectionList, inspectionPropertyName, filteredImages) {
    inspectionList.forEach((inspectionItem, inspectionIndex) => {
      const currLocation = inspectionItem.location;
      const newImgList = [];
      filteredImages.forEach((item) => {
        //incase of damage form, its location can be same so using its index value to compare
        const isValidImage = ((inspectionPropertyName === 'exceptionImages') && (item.damageIndex >= 0)) ?
          (inspectionIndex === item.damageIndex) :
          (currLocation === item.location);
        if (isValidImage) {
          newImgList.push({
            url: (item.url || item.sourceUrl)
          });
        }
      });
      inspectionItem[inspectionPropertyName] = newImgList;
    });
  }

  public restrictSomeChars(key) {
    if (key.match(/^[^~";^|]$/)) {
      return true;
    }
    return false;
  }

  /** Validate Role and return selected DealershipUuid to represent
   * If Admin or Lead Seller, return selected sellerOeUuid
   * If Ambassador, return ambassador selected dealership from State.
   */
  getSelectedDealershipUuid(sellerOeUuid?) {
    if (this.isAdmin || this.isLeadSeller) {
      return sellerOeUuid;
    }
    const isAmbassador = this.store.selectSnapshot(ConfigurationState.hasRole(this.AMBASSADOR));
    const dealerUuid = this.store.selectSnapshot(AmbassadorState.selectedAmbassadorDealershipUuid);
    return isAmbassador && dealerUuid ? dealerUuid : null;
  }

  get isAdmin(): boolean {
    return  this.store.selectSnapshot(ConfigurationState.isAdmin());
  }

  get isLeadSeller() {
    return this.store.selectSnapshot(ConfigurationState.hasRole(this.LEAD_SELLER));
  }

  get isPriceAdmin(): boolean{
    return this.store.selectSnapshot(ConfigurationState.hasRole(this.ALLY_PRICE_ADMIN));
  }

  get isAmbassadorUser(): boolean{
    return this.store.selectSnapshot(ConfigurationState.hasRole(this.AMBASSADOR));
  }
}
